import { Apple, Download, Monitor, ShieldCheck, Terminal, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useAgentReleases } from "@/api/queries";
import { PageHeader } from "@/components/common/PageHeader";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";

const platforms: { key: "macos" | "windows" | "linux"; icon: LucideIcon }[] = [
  { key: "macos", icon: Apple },
  { key: "windows", icon: Monitor },
  { key: "linux", icon: Terminal },
];

const steps = ["install", "signIn", "offer", "start"] as const;

/** Downloads of the desktop tracker: the latest published build of every platform. */
export function DownloadPage() {
  const { t } = useTranslation();
  const releases = useAgentReleases();
  const latest = (releases.data ?? []).filter((r) => r.is_latest);
  const any = latest.length > 0;
  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <PageHeader title={t("download.title")} description={t("download.subtitle")} />
      {releases.data && !any && <Alert>{t("download.notReady")}</Alert>}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {platforms.map(({ key, icon: Icon }) => {
          const builds = latest.filter((r) => r.os === key);
          return (
            <Card key={key} className="flex flex-col gap-3 p-5">
              <div className="flex items-center justify-between">
                <span className="flex size-10 items-center justify-center rounded-lg bg-primary-soft text-primary">
                  <Icon className="size-5" aria-hidden="true" />
                </span>
                {builds[0] ? (
                  <Badge variant="success">{t("download.version", { version: builds[0].version })}</Badge>
                ) : (
                  <Badge>{t("download.soon")}</Badge>
                )}
              </div>
              <div>
                <h2 className="text-base font-semibold">{t(`download.platforms.${key}`)}</h2>
                <p className="text-xs text-muted">{t(`download.requirements.${key}`)}</p>
              </div>
              <div className="mt-auto flex flex-col gap-2">
                {builds.length === 0 ? (
                  <Button variant="secondary" disabled>
                    {t("nav.downloadTracker")}
                  </Button>
                ) : (
                  builds.map((b) => (
                    <Button key={b.id} asChild variant={b === builds[0] ? "primary" : "secondary"}>
                      <a href={b.url} rel="noreferrer">
                        <Download />
                        {builds.length > 1
                          ? t("download.forArch", { arch: t(`settings.releases.arch.${b.arch}`) })
                          : t("nav.downloadTracker")}
                      </a>
                    </Button>
                  ))
                )}
                {builds[0] && (
                  <p className="truncate font-mono text-[11px] text-muted" title={builds[0].sha256}>
                    {t("settings.releases.checksum", { value: builds[0].sha256.slice(0, 16) })}
                  </p>
                )}
              </div>
            </Card>
          );
        })}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>{t("download.stepsTitle")}</CardTitle>
          </CardHeader>
          <ol className="flex flex-col gap-3 px-5 pb-5">
            {steps.map((s, i) => (
              <li key={s} className="flex gap-3">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-semibold text-primary">
                  {i + 1}
                </span>
                <span>{t(`download.steps.${s}`)}</span>
              </li>
            ))}
          </ol>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="size-5 text-success" aria-hidden="true" />
              {t("download.privacyTitle")}
            </CardTitle>
          </CardHeader>
          <div className="flex flex-col gap-3 px-5 pb-5">
            <p className="text-muted">{t("download.privacyText")}</p>
            <Link to="/settings/security" className="font-medium text-primary hover:underline">
              {t("download.privacyLink")}
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
